import React, { Component, useState } from "react";
import styled from "styled-components";

import { AdvanceSlideOnType } from "common/constants";

import {
    Mouse as MouseIcon,
    MusicNote as MusicNoteIcon,
    Timer as TimerIcon,
    ArrowBackIos as ArrowBackIosIcon,
    ArrowForwardIos as ArrowForwardIosIcon
} from "@material-ui/icons";

const defaultSlideState = {
    advanceOn: AdvanceSlideOnType.CLICK,
    advanceDelay: 5
};

const Container = styled.div`
    display: flex;
    flex-flow: column;
    justify-content: flex-start;
    align-items: flex-start;
    text-transform: uppercase;
    font-size: 14px;
    font-weight: 600;
    color: #333;
`;

const AdvanceTypeSelectContainer = styled.div`
    display: flex;
    justify-content: flex-start;
    align-items: center;

    span:first-child {
        width: 210px;
    }
`;

const AdvanceTypeSelectButtonContainer = styled.div.attrs(({ selected }) => ({
    style: {
        background: selected ? "#11a9e2" : "#ffffff",
        border: `1px solid ${selected ? "#11a9e2" : "#ccc"}`
    }
}))`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 42px;
    height: 42px;
    margin-left: 20px;
    cursor: pointer;

    &:hover {
        opacity: 0.9;
    }
`;

const AdvanceTypeSelectButtonTooltip = styled.div`
    position: absolute;
    left: 50%;
    bottom: 0;
    transform: translate(-50%, 120%);
    font-size: 11px;
`;

function AdvanceTypeSelectButton({ type, selected, onClick }) {
    const [showTooltip, setShowTooltip] = useState(false);

    const Icon = type === AdvanceSlideOnType.CLICK
        ? MouseIcon
        : type === AdvanceSlideOnType.DELAY
            ? TimerIcon
            : MusicNoteIcon;

    return (
        <AdvanceTypeSelectButtonContainer
            selected={selected}
            onClick={onClick}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
        >
            <Icon style={{ color: selected ? "#ffffff" : "#666666" }} />
            {showTooltip && <AdvanceTypeSelectButtonTooltip>{type}</AdvanceTypeSelectButtonTooltip>}
        </AdvanceTypeSelectButtonContainer>
    );
}

function AdvanceTypeSelect({ currentType, availableTypes, onChange }) {
    return (
        <AdvanceTypeSelectContainer>
            <span>Advance to next slide after</span>
            {availableTypes.map(type => (
                <AdvanceTypeSelectButton
                    key={type}
                    type={type}
                    selected={type === currentType}
                    onClick={() => onChange(type)}
                />
            ))}
        </AdvanceTypeSelectContainer>
    );
}

const AdvanceDelayControlContainer = styled.div`
    display: flex;
    justify-content: flex-start;
    align-items: center;
    margin-top: 9px;

    span:first-child {
        width: 210px;
    }
`;

const AdvanceDelayControlInput = styled.input`
    width: 60px;
    height: 32px;
    border: solid 1px #ccc;
    background: white;
    text-align: center;
    outline: none;
    margin: 0px 2px 0px 2px;

    ::-webkit-outer-spin-button,
    ::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }
    -moz-appearance: textfield;
`;

const AdvanceDelayControlBackButton = styled(ArrowBackIosIcon)`
    &&& {
        color: #ccc;
        cursor: pointer;
        font-size: 16px;
        margin-left: 2px;

        &:hover {
            color: #11a9e2;
        }
    }
`;

const AdvanceDelayControlForwardButton = styled(ArrowForwardIosIcon)`
    &&& {
        color: #ccc;
        cursor: pointer;
        font-size: 16px;
        margin-right: 20px;
        margin-left: 2px;

        &:hover {
            color: #11a9e2;
        }
    }
`;

function AdvanceDelayControl({ currentDelay, onChange }) {
    const handleChange = newValue => {
        const delay = Math.clamp(parseInt(newValue) || 0, 0, 1000);
        onChange(delay);
    };

    return (
        <AdvanceDelayControlContainer>
            <span>Advance after</span>
            <AdvanceDelayControlBackButton onClick={() => handleChange(currentDelay - 1)} />
            <AdvanceDelayControlInput type="number" value={currentDelay} onChange={event => handleChange(event.target.value)} />
            <AdvanceDelayControlForwardButton onClick={() => handleChange(currentDelay + 1)} />
            <span>seconds</span>
        </AdvanceDelayControlContainer>
    );
}

export class SlideOptionsPanel extends Component {
    constructor() {
        super();

        this.state = {
            ...defaultSlideState
        };
    }

    componentDidMount() {
        this.loadSlideState();
    }

    loadSlideState = () => {
        const { canvas } = this.props;

        let advanceOn = canvas.dataModel.get("advanceOn") ?? defaultSlideState.advanceOn;
        if (advanceOn === AdvanceSlideOnType.AUDIO && !canvas.hasAudio()) {
            advanceOn = AdvanceSlideOnType.CLICK;
        }

        this.setState({
            advanceOn,
            advanceDelay: canvas.dataModel.get("advanceDelay") ?? defaultSlideState.advanceDelay
        });
    }

    updateSlideState({ advanceOn = undefined, advanceDelay = undefined }) {
        const { canvas } = this.props;

        const updates = {};
        if (advanceOn !== undefined) {
            updates.advanceOn = advanceOn;
        }
        if (advanceDelay !== undefined) {
            updates.advanceDelay = advanceDelay;
        }

        canvas.dataModel.update(updates);
        this.loadSlideState();
    }

    render() {
        const { canvas } = this.props;
        const { advanceOn, advanceDelay } = this.state;

        const availableAdvanceOnTypes = Object.values(AdvanceSlideOnType)
            .filter(type => (type === AdvanceSlideOnType.AUDIO && canvas.hasAudio()) || type !== AdvanceSlideOnType.AUDIO);

        return (
            <Container>
                <AdvanceTypeSelect
                    currentType={advanceOn}
                    availableTypes={availableAdvanceOnTypes}
                    onChange={advanceOn => this.updateSlideState({ advanceOn })}
                />
                {advanceOn === AdvanceSlideOnType.DELAY &&
                    <AdvanceDelayControl currentDelay={advanceDelay} onChange={advanceDelay => this.updateSlideState({ advanceDelay })} />
                }
            </Container>
        );
    }
}
